import { Component, inject, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChatMessageComponent, ChatMessageGroupComponent, GalaxyChatModule } from '@vendasta/galaxy/chat';
import { map, Observable, of, tap } from 'rxjs';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { CallRecord } from '@vendasta/voice';
import { AssistantInterface } from '@vendasta/ai-assistants';
import { shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { CrmInjectionToken } from '../../../tokens-and-interfaces';

@Component({
  selector: 'crm-call-transcript-modal',
  templateUrl: './call-transcript-modal.component.html',
  styleUrl: './call-transcript-modal.component.scss',
  standalone: true,
  imports: [
    MatDialogModule,
    TranslateModule,
    DatePipe,
    CommonModule,
    ChatMessageComponent,
    ChatMessageGroupComponent,
    GalaxyChatModule,
    GalaxyPopoverModule,
    MatIcon,
    MatIconButton,
  ],
})
export class CallTranscriptModalComponent {
  transcript = this.buildTranscriptFromJSON(this.data.transcriptJSON);

  readonly OPENAI_BOT_BACKGROUND_COLOR = '#f6f6f6';

  callRecord$: Observable<CallRecord | undefined>;
  callRecordHasVoiceSettings$: Observable<boolean>;

  assistantThatHandledCall$: Observable<AssistantInterface | undefined>;
  protected readonly config = inject(CrmInjectionToken);
  OPENAI_BOT_SVG_ICON = this.config.services?.defaultAssistantAvatarIcon;

  protected readonly assistantService = this.config.services?.aiAssistantService;
  protected readonly voiceApi = this.config.services?.voiceApiService;

  assistantProfilePic$: Observable<string | undefined>;
  assistantLoading$: Observable<boolean>;

  show = signal(false);

  route = inject(ActivatedRoute);
  showVoiceConfig$: Observable<boolean> = this.route.queryParamMap.pipe(
    tap((params) => console.log('query params map:', params)),
    map((params) => params.get('debug') === 'true'),
  );

  constructor(
    public dialogRef: MatDialogRef<CallTranscriptModalComponent>,
    // Is there a way to update this? fields of CallData could be Observables?
    @Inject(MAT_DIALOG_DATA) public data: CallData,
  ) {
    const namespace = data.accountGroupId
      ? {
          accountGroupNamespace: {
            accountGroupId: data.accountGroupId,
          },
        }
      : data.partnerId
        ? {
            partnerNamespace: {
              partnerId: data.partnerId,
            },
          }
        : undefined;
    if (data.callRecordId && namespace) {
      this.callRecord$ =
        this.voiceApi
          ?.getCallRecord({
            id: data.callRecordId,
            namespace,
          })
          .pipe(
            map((resp) => resp.callRecord),
            shareReplay({ refCount: true, bufferSize: 1 }),
          ) ?? of(undefined);
    } else {
      this.callRecord$ = of(undefined);
    }

    this.callRecordHasVoiceSettings$ = this.callRecord$.pipe(
      map((callRecord) => {
        const config = callRecord?.metadata?.voiceConfig?.openaiRealtimeConfig;
        return Boolean(
          config?.voice ||
            config?.turnDetection?.silenceDuration ||
            config?.turnDetection?.prefixPadding ||
            config?.turnDetection?.threshold,
        );
      }),
    );

    if (data.partnerId && data.accountGroupId) {
      const agid = data.accountGroupId;
      const pid = data.partnerId;
      this.assistantThatHandledCall$ = this.callRecord$.pipe(
        switchMap((callRecord) => {
          if (!this.assistantService) return of(undefined);
          return this.assistantService.getAssistant(
            pid,
            agid,
            callRecord?.metadata?.assistantId || 'ASSISTANT-voice-receptionist',
          );
        }),
      );
    } else {
      this.assistantThatHandledCall$ = of(undefined);
    }

    const assistantDetails$ = this.assistantThatHandledCall$.pipe(
      map((assistant) => ({
        avatarUrl: assistant?.avatarUrl ?? undefined,
        loading: false,
      })),
      startWith({ avatarUrl: undefined, loading: true }),
    );
    this.assistantProfilePic$ = assistantDetails$.pipe(map((details) => details.avatarUrl));
    this.assistantLoading$ = assistantDetails$.pipe(map((details) => details.loading));
  }

  closeDialog() {
    this.dialogRef.close();
  }

  private buildTranscriptFromJSON(transcriptJSON: string): TranscriptMessageGroup[] {
    const messages: TranscriptMessage[] = JSON.parse(transcriptJSON);
    if (!messages || messages.length === 0) {
      return [];
    }
    const results: TranscriptMessageGroup[] = [
      {
        name: messages[0].name,
        messages: [messages[0]],
      },
    ];
    messages.shift();
    for (const message of messages) {
      if (message.name === results[results.length - 1].name) {
        results[results.length - 1].messages.push(message);
      } else {
        results.push({
          name: message.name,
          messages: [message],
        });
      }
    }
    return results;
  }

  protected readonly PopoverPositions = PopoverPositions;
}

interface CallData {
  partnerId: string;
  accountGroupId?: string;
  callRecordId: string | undefined;
  transcriptJSON: string;
  toName: string;
  fromName: string;
}

interface TranscriptMessage {
  content: string;
  timestamp: string;
  name: string;
}

interface TranscriptMessageGroup {
  name: string;
  messages: TranscriptMessage[];
}
